import { useState, useEffect, useRef, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';
import { Text } from './Text';

const toastVariants = cva(
  'relative flex items-start gap-3 rounded-lg px-4 py-3 shadow-xl border transition-all duration-300 min-w-[300px] max-w-[400px] touch-pan-y backdrop-blur-sm',
  {
    variants: {
      variant: {
        success:
          'bg-success/90 border-success/30 text-success-foreground dark:bg-success/85 dark:text-white',
        error:
          'bg-destructive/90 border-destructive/30 text-destructive-foreground dark:bg-destructive/85 dark:text-white',
        info: 'bg-info/90 border-info/30 text-info-foreground dark:bg-info/85 dark:text-white',
        achievement:
          'bg-primary/90 border-primary/30 text-primary-foreground dark:bg-primary/85 dark:text-white',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  }
);

export interface ToastData {
  id: string;
  title: string;
  description?: string;
  variant: 'success' | 'error' | 'info' | 'achievement';
  duration?: number | 'persistent';
  action?: {
    label: string;
    onClick: () => void;
  };
  position?: ToastPosition;
}

export type ToastPosition =
  | 'top-right'
  | 'top-center'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left';

interface ToastProps extends VariantProps<typeof toastVariants> {
  toast: ToastData;
  onClose: (id: string) => void;
  position: ToastPosition;
}

const positionClasses: Record<ToastPosition, string> = {
  'top-right': 'top-20 right-4 sm:top-6 sm:right-6', // More space from top to avoid header elements
  'top-center': 'top-20 left-1/2 -translate-x-1/2 sm:top-6',
  'top-left': 'top-20 left-4 sm:top-6 sm:left-6',
  'bottom-right': 'bottom-4 right-4 sm:bottom-6 sm:right-6',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 sm:bottom-6',
  'bottom-left': 'bottom-4 left-4 sm:bottom-6 sm:left-6',
};

const Toast = ({ toast, onClose, position }: ToastProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const toastRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onClose(toast.id), 300);
  }, [onClose, toast.id]);

  // Auto-close logic
  useEffect(() => {
    setIsVisible(true);

    if (toast.duration !== 'persistent') {
      const timer = setTimeout(handleClose, toast.duration || getDurationByType(toast.variant));
      return () => clearTimeout(timer);
    }
  }, [handleClose, toast.duration, toast.variant]);

  // Touch/swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startXRef.current;
    setDragX(diff);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // If swiped more than 100px, dismiss
    if (Math.abs(dragX) > 100) {
      handleClose();
    } else {
      setDragX(0);
    }
  };

  const getIcon = () => {
    switch (toast.variant) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />;
      case 'info':
        return <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />;
      case 'achievement':
        return <TrophyIcon className="h-5 w-5 flex-shrink-0" />;
    }
  };

  const getTransform = () => {
    const baseTransform = position.includes('right')
      ? 'translate-x-full'
      : position.includes('left')
        ? '-translate-x-full'
        : 'translate-y-full';
    const visibleTransform = isVisible ? 'translate-x-0 translate-y-0' : baseTransform;
    const dragTransform = dragX !== 0 ? `translateX(${dragX}px)` : '';
    return `${visibleTransform} ${dragTransform}`;
  };

  return (
    <div
      ref={toastRef}
      className={cn(
        toastVariants({ variant: toast.variant }),
        'opacity-0 transition-all duration-300',
        isVisible && 'opacity-100',
        isDragging && 'transition-none'
      )}
      style={{
        transform: getTransform(),
        opacity: Math.max(0, 1 - Math.abs(dragX) / 200),
      }}
      role="alert"
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {getIcon()}
      <div className="flex-1">
        <Text variant="body" weight="medium" size="sm" className="mb-0.5">
          {toast.title}
        </Text>
        {toast.description && (
          <Text variant="body" size="sm" className="text-current">
            {toast.description}
          </Text>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-2 text-sm font-medium underline hover:no-underline text-current hover:brightness-110 transition-all"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={handleClose}
        className="flex-shrink-0 ml-2 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        aria-label="Close notification"
      >
        <XMarkIcon className="h-4 w-4 text-current hover:brightness-110" />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastData[];
  onRemove: (id: string) => void;
  defaultPosition?: ToastPosition;
}

export const ToastContainer = ({
  toasts,
  onRemove,
  defaultPosition = 'bottom-right',
}: ToastContainerProps) => {
  // Group toasts by position
  const groupedToasts = toasts.reduce(
    (acc, toast) => {
      const position = toast.position || defaultPosition;
      if (!acc[position]) acc[position] = [];
      acc[position].push(toast);
      return acc;
    },
    {} as Record<ToastPosition, ToastData[]>
  );

  return (
    <>
      {Object.entries(groupedToasts).map(([position, positionToasts]) => (
        <div
          key={position}
          className={cn(
            'fixed z-50 pointer-events-none',
            positionClasses[position as ToastPosition]
          )}
        >
          <div
            className={cn(
              'flex flex-col pointer-events-auto',
              position.includes('bottom') ? 'flex-col-reverse' : 'flex-col',
              'gap-2'
            )}
          >
            {positionToasts.map(toast => (
              <Toast
                key={toast.id}
                toast={toast}
                onClose={onRemove}
                position={position as ToastPosition}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

// Helper function to get duration by toast type
export const getDurationByType = (type: ToastData['variant']): number => {
  switch (type) {
    case 'error':
      return 6000; // 6 seconds for errors
    case 'achievement':
      return 5000; // 5 seconds for achievements
    case 'info':
      return 4000; // 4 seconds for info
    case 'success':
    default:
      return 3000; // 3 seconds for success
  }
};
